import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield,
  LogOut,
  Key,
  CheckCircle2,
  ChevronRight,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuthContext } from "@/providers/AuthProvider";
import { PasswordChangeForm } from "./PasswordChangeForm";
import { MfaSetupSection } from "./MfaSetupSection";
import { GoogleLinkSection } from "./GoogleLinkSection";

interface AccountTabProps {
  onClose: () => void;
}

export function AccountTab({ onClose }: AccountTabProps) {
  const { logout, clientInfo, clientInfoLoading } = useAuthContext();
  const [showPasswordChange, setShowPasswordChange] = useState(false);

  const handleSignOut = () => {
    onClose();
    logout();
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="border border-border rounded-lg overflow-hidden">
          <button
            onClick={() => setShowPasswordChange(!showPasswordChange)}
            className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Key className="h-5 w-5 text-primary" />
              </div>
              <div className="text-left">
                <p className="font-medium text-sm">Password</p>
                <p className="text-xs text-muted-foreground">
                  Change your account password
                </p>
              </div>
            </div>
            <ChevronRight
              className={`h-5 w-5 text-muted-foreground transition-transform ${
                showPasswordChange ? "rotate-90" : ""
              }`}
            />
          </button>

          <AnimatePresence>
            {showPasswordChange && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="p-4 border-t border-border bg-muted/30">
                  <PasswordChangeForm
                    onSuccess={() => setShowPasswordChange(false)}
                    hasMfaEnabled={clientInfo?.isMFAEnabled ?? false}
                    email={clientInfo?.email ?? ""}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="border border-border rounded-lg p-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="font-medium text-sm">Two-Factor Authentication</p>
              <p className="text-xs text-muted-foreground">
                Add an extra layer of security
              </p>
            </div>
          </div>

          {clientInfoLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading...
            </div>
          ) : clientInfo?.isMFAEnabled ? (
            <div className="flex items-center gap-2 text-sm text-emerald-500">
              <CheckCircle2 className="h-4 w-4" />
              MFA is enabled
            </div>
          ) : (
            <MfaSetupSection email={clientInfo?.email ?? ""} />
          )}
        </div>

        <GoogleLinkSection />
      </div>

      <div className="pt-4 border-t border-border">
        <Button
          variant="destructive"
          onClick={handleSignOut}
          className="w-full"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Sign Out
        </Button>
      </div>
    </div>
  );
}
